import { useEffect, useState } from 'react';
import { SingleValuePanel, type PanelSpan } from '@/components/shared';
import { fetchBackofficeUserCommunicationSummary } from '../api';
import { formatRelativeLastSeen, formatShortId } from '../format';
import type { BackofficeUserCommunicationSummary } from '../types';

export interface UserCommunicationSummaryTilesProps {
  summary: BackofficeUserCommunicationSummary | null;
  loading: boolean;
  error: string | null;
}

export interface UserCommunicationSummaryPanelProps extends UserCommunicationSummaryTilesProps {
  surface?: 'page' | 'tile' | undefined;
}

export interface UserCommunicationSummaryTileProps {
  userId: string;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function formatEnabled(value: boolean): string {
  return value ? 'On' : 'Off';
}

function useRelativeEmailTime(value: string | null | undefined) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  return formatRelativeLastSeen(value ?? null, now);
}

export function UserCommunicationPreferencesPanel({
  summary,
  loading,
  error,
  surface = 'page',
}: UserCommunicationSummaryPanelProps) {
  return (
    <section
      className={
        surface === 'tile'
          ? 'h-full overflow-y-auto bg-white'
          : 'rounded-lg border border-neutral-200 bg-white p-5 shadow-sm xl:col-span-3'
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-body font-semibold text-neutral-900">Communication preferences</h2>
          <p className="mt-1 text-small text-neutral-500">
            Global and study-specific email settings
          </p>
        </div>

        {loading ? (
          <p className="text-body text-neutral-500">Loading preferences</p>
        ) : error ? (
          <p className="text-body text-red-700">{error}</p>
        ) : summary ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="text-small font-semibold uppercase text-neutral-500">General</h3>
              <dl className="mt-2 grid gap-2">
                {summary.preferences.email.map((preference) => (
                  <div key={preference.key} className="flex items-center justify-between gap-3">
                    <dt className="text-body text-neutral-700">{preference.label}</dt>
                    <dd
                      className={[
                        'rounded-full px-2.5 py-1 text-small font-semibold',
                        preference.enabled
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-neutral-100 text-neutral-600',
                      ].join(' ')}
                    >
                      {formatEnabled(preference.enabled)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h3 className="text-small font-semibold uppercase text-neutral-500">Studies</h3>
              {summary.preferences.studies.length === 0 ? (
                <p className="mt-2 text-body text-neutral-500">No study-specific overrides.</p>
              ) : (
                <div className="mt-2 max-h-48 overflow-y-auto pr-2">
                  <ul className="divide-y divide-neutral-100">
                    {summary.preferences.studies.map((study) => (
                      <li key={study.projectId} className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 truncate text-body font-medium text-neutral-900">
                            {study.studyName}
                          </span>
                          <span className="flex-none text-small font-semibold text-neutral-600">
                            {formatEnabled(study.enabled)}
                          </span>
                        </div>
                        <p className="mt-1 text-small text-neutral-500">
                          Completed interviews {formatEnabled(study.completedInterview)} | Analysis
                          runs {formatEnabled(study.analysisRunCompleted)} |{' '}
                          {formatShortId(study.projectId)}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function UserLastEmailSentPanel({ summary, loading }: UserCommunicationSummaryTilesProps) {
  const lastEmail = useRelativeEmailTime(summary?.lastSentEmailAt);

  return (
    <SingleValuePanel
      title="Last email sent"
      value={loading ? 'Loading' : lastEmail.value}
      footer={loading ? undefined : (lastEmail.suffix ?? undefined)}
      valueTone={summary?.lastSentEmailAt ? 'primary' : 'neutral'}
    />
  );
}

export function UserCommunicationSummaryTiles(props: UserCommunicationSummaryTilesProps) {
  return (
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
      <UserCommunicationPreferencesPanel {...props} />
      <UserLastEmailSentPanel {...props} />
    </div>
  );
}

function useUserCommunicationSummary(userId: string) {
  const [summary, setSummary] = useState<BackofficeUserCommunicationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchBackofficeUserCommunicationSummary(userId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setSummary(response);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setSummary(null);
        setError(caught instanceof Error ? caught.message : 'Failed to load preferences');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [userId]);

  return { summary, loading, error };
}

export function UserCommunicationPreferencesTile({ userId }: UserCommunicationSummaryTileProps) {
  const summaryState = useUserCommunicationSummary(userId);

  return <UserCommunicationPreferencesPanel {...summaryState} surface="tile" />;
}

export function UserLastEmailSentTile({
  userId,
  panelId,
  currentSpan,
}: UserCommunicationSummaryTileProps) {
  const summaryState = useUserCommunicationSummary(userId);
  const lastEmail = useRelativeEmailTime(summaryState.summary?.lastSentEmailAt);

  return (
    <SingleValuePanel
      panelId={panelId}
      currentSpan={currentSpan}
      title="Last email sent"
      value={summaryState.loading ? 'Loading' : lastEmail.value}
      footer={summaryState.loading ? undefined : (lastEmail.suffix ?? undefined)}
      valueTone={summaryState.summary?.lastSentEmailAt ? 'primary' : 'neutral'}
    />
  );
}
